import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Img } from 'components/common/Img';
import { ColDiv, RowDiv, Text } from 'styles';
import { Icon } from 'components/common/Icon';

export function MainVisual({ list }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [play, setPlay] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      setCurrentIndex(ref.current.swiper.activeIndex);
    }
    if (play) {
      ref.current.swiper.autoplay.stop();
    } else {
      ref.current.swiper.autoplay.start();
    }
  }, [ref.current, play]);

  return (
    <Container>
      <Swiper
        ref={ref}
        autoplay={{
          delay: 3000,
        }}
        navigation={{
          prevEl: '.prev',
          nextEl: '.next',
        }}
        centeredSlides="true"
        modules={[Autoplay, Navigation]}
        onSlideChange={(swiper) => {
          setCurrentIndex(swiper.activeIndex);
        }}
      >
        {list.map((item, index) => (
          <SwiperSlide key={`item_${index}`}>
            <Img src={item.img} alt={`주식회사 팜인 - ${item.title} (축사 환기·악취 저감·제습 기술)`} $width="100vw" $height="100vh" $objectFit="cover" $objectPosition="bottom" />
            <TextBox>
              <Text $type="h2" $color="white">
                {item.title}
              </Text>
              <Text $type="b4" $mType="b7" $color="white">
                {item.desc}
              </Text>
            </TextBox>
          </SwiperSlide>
        ))}
      </Swiper>
      <SwiperPagination>
        <Text $color="white" $type="h5">
          {currentIndex + 1}
        </Text>
        <ProgressBox $index={Number(currentIndex + 1)} $total={list.length} />
        <Text $color="rgba(255, 255, 255, 0.33)" $type="h5">
          {list.length}
        </Text>
        <RowDiv $gap={8}>
          <Icon name={(!play && 'pause') || 'play'} onClick={() => setPlay(!play)} />
          <Icon
            className="prev"
            name="arrow"
            $transform="rotate(90deg)"
            $pathStroke="white"
            $strokeOpacity={1}
            $pointer
          />
          <Icon
            className="next"
            name="arrow"
            $transform="rotate(-90deg)"
            $pathStroke="white"
            $strokeOpacity={1}
            $pointer
          />
        </RowDiv>
      </SwiperPagination>
      <MoSwiperPagination>
        <RowDiv>
          <Text $color="white" $type="h5">
            {currentIndex + 1}
          </Text>
          <Text $color="white" $type="b5">
            &nbsp;/ {list.length}
          </Text>
        </RowDiv>
        <MoProgressBox $index={Number(currentIndex + 1)} $total={list.length} />
      </MoSwiperPagination>
    </Container>
  );
}

const Container = styled.div`
  position: relative;
`;

const TextBox = styled(ColDiv)`
  position: absolute;
  top: calc(50% - 66px);
  left: 50%;
  transform: translateX(-50%);
  gap: 16px;

  @media ${({ theme }) => theme.device.tablet} {
    width: 100%;
    top: calc(50% - 132px);
    padding: 0 87px;
  }
  @media ${({ theme }) => theme.device.mobile} {
    padding: 0 48px;
  }
`;

const SwiperPagination = styled(RowDiv)`
  align-items: center;
  position: absolute;
  bottom: 48px;
  left: 50%;
  transform: translateX(-50%);
  gap: 16px;
  z-index: 3;

  & > p {
    min-width: 13px;
  }

  @media ${({ theme }) => theme.device.mobile} {
    display: none;
  }
`;

const MoSwiperPagination = styled(ColDiv)`
  display: none;

  @media ${({ theme }) => theme.device.mobile} {
    display: flex;
    width: 100%;
    align-items: center;
    position: absolute;
    bottom: 48px;
    left: 50%;
    transform: translateX(-50%);
    gap: 16px;
    z-index: 3;
    padding: 0 24px;
    align-items: flex-end;

    & > div:nth-child(1) {
      background-color: rgba(23, 23, 23, 0.7);
      padding: 4px 8px;
      border-radius: 8px;
      & > p {
        min-width: 13px;
      }
    }
  }
`;

const ProgressBox = styled.div`
  width: 99px;
  background-color: rgba(255, 255, 255, 0.33);
  border-radius: 4px;
  height: 2px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    width: ${({ $index, $total }) => `${(Math.max($index, 1) / $total) * 100}%`};
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const MoProgressBox = styled.div`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.33);
  border-radius: 4px;
  height: 2px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    width: ${({ $index, $total }) => `${(Math.max($index, 1) / $total) * 100}%`};
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
  }
`;
